import z from "zod";

import { migrateBatteryAlertEvent } from "./migrators/device/device";
import { buildDataSchema } from "./schemas/build";
import {
    deviceBatteryHealthDegradedDataSchema,
    deviceDataSchema,
    deviceLocationDataSchema,
    deviceMemoryDataSchema,
    deviceRebootedDataSchema,
    deviceUpdatedDataSchema,
} from "./schemas/device";
import { groupDataSchema } from "./schemas/group";
import { licenseMovedSchema, licenseReasonDataSchema, licenseUserDataSchema } from "./schemas/license";
import { productChangedDataSchema } from "./schemas/product";
import { subscriptionCancelledDataSchema } from "./schemas/subscription";
import { workspaceConfigurationChangedDataSchema } from "./schemas/workspace";
import { NormalizedEventReport } from "./eventVersionService.types";

type EventTypeName = API.EventReport["eventTypeName"];
type EventMigrator = (event: API.EventReport) => NormalizedEventReport;

class EventVersionService {
    private readonly version = 2 as const;

    private readonly migrators: Record<EventTypeName, EventMigrator> = {
        "BuildEvent.Failed": (event) => this.validateOrNull(event, buildDataSchema),
        "BuildEvent.Succeeded": (event) => this.validateOrNull(event, buildDataSchema),
        "DeviceEvent.Activated": (event) => this.stampVersion(event),
        "DeviceEvent.BatteryAlertDetected": (event) => migrateBatteryAlertEvent(event as API.DeviceBatteryAlertEvent),
        "DeviceEvent.BatteryHealthDegraded": (event) => this.validateOrNull(event, deviceBatteryHealthDegradedDataSchema),
        "DeviceEvent.BatteryLevelChanged": (event) => this.stampVersion(event),
        "DeviceEvent.Created": (event) => this.validateOrNull(event, deviceDataSchema),
        "DeviceEvent.Deleted": (event) => this.validateOrNull(event, deviceDataSchema),
        "DeviceEvent.LocationChanged": (event) => this.validateOrNull(event, deviceLocationDataSchema),
        "DeviceEvent.MemoryAlertDetected": (event) => this.validateOrNull(event, deviceMemoryDataSchema),
        "DeviceEvent.MemoryLevelChanged": (event) => this.stampVersion(event),
        "DeviceEvent.Rebooted": (event) => this.validateOrNull(event, deviceRebootedDataSchema),
        "DeviceEvent.Renamed": (event) => this.validateOrNull(event, deviceDataSchema),
        "DeviceEvent.Updated": (event) => this.validateOrNull(event, deviceUpdatedDataSchema),
        "FileEvent.Deleted": (event) => this.stampVersion(event),
        "FileEvent.Renamed": (event) => this.stampVersion(event),
        "GroupEvent.Created": (event) => this.validateOrNull(event, groupDataSchema),
        "GroupEvent.Deleted": (event) => this.validateOrNull(event, groupDataSchema),
        "GroupEvent.Renamed": (event) => this.validateOrNull(event, groupDataSchema),
        "LicenseEvent.Created": (event) => this.validateOrNull(event, licenseUserDataSchema),
        "LicenseEvent.Deleted": (event) => this.validateOrNull(event, licenseReasonDataSchema),
        "LicenseEvent.Linked": (event) => this.validateOrNull(event, licenseUserDataSchema),
        "LicenseEvent.Moved": (event) => this.validateOrNull(event, licenseMovedSchema),
        "LicenseEvent.Revived": (event) => this.validateOrNull(event, licenseReasonDataSchema),
        "LicenseEvent.Revoked": (event) => this.validateOrNull(event, licenseReasonDataSchema),
        "LicenseEvent.Unlinked": (event) => this.validateOrNull(event, licenseReasonDataSchema),
        "ProductEvent.Changed": (event) => this.validateOrNull(event, productChangedDataSchema),
        "ProductEvent.Deleted": (event) => this.stampVersion(event),
        "ProvisioningEvent.Changed": (event) => this.stampVersion(event),
        "SubscriptionEvent.Cancelled": (event) => this.validateOrNull(event, subscriptionCancelledDataSchema),
        "WorkspaceEvent.ConfigurationChanged": (event) => this.validateOrNull(event, workspaceConfigurationChangedDataSchema),
    };

    private normalize(event: API.EventReport): NormalizedEventReport {
        const migrator = this.migrators[event.eventTypeName];
        if (!migrator) {
            // eslint-disable-next-line no-console
            console.warn(`[EventVersionService] No migrator for event type: ${event.eventTypeName}`);
            return event as NormalizedEventReport;
        }
        return migrator(event);
    }

    public normalizeList(events: API.EventReport[]): NormalizedEventReport[] {
        return events.map((event) => this.normalize(event));
    }

    private stampVersion(event: API.EventReport): NormalizedEventReport {
        return { ...event, dataVersion: this.version } as NormalizedEventReport;
    }

    private validateOrNull(event: API.EventReport, schema: z.ZodSchema): NormalizedEventReport {
        if (schema.safeParse(event.data).success) {
            return this.stampVersion(event);
        }
        return this.stampVersion({ ...event, data: null });
    }
}

export const eventVersionService = new EventVersionService();
